import { useTranslation } from 'react-i18next'
import GenericCard from '../../../../components/GenericCard'
import MaintenanceIcon from '../../../../assets/svg/maintenanceIcon.svg'
import { Box, styled, Typography, useTheme } from '@mui/material'
import { MUIThemeColors } from '../../../../mui-theme'
import { GetRGBA, getThemeColor } from '../../../../utils'
import { MaintenanceType } from '../../../../enums'
import { useParams } from 'react-router-dom'
import { IMaintenanceTypeCount } from '../../../../generated-types/IMaintenanceTypesCount'
import { useState } from 'react'
import { LocalityMaintenanceGrid } from '../../../../components/locality-dashboard/LocalityMaintenanceGrid'
import { useGetMaintenancesWithCounts } from 'src/api/localities'
import ConstructionIcon from '@mui/icons-material/Construction'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import DomainVerificationIcon from '@mui/icons-material/DomainVerification'
import AssessmentIcon from '@mui/icons-material/Assessment'
import { SvgIconComponent } from '@mui/icons-material'

export const mapMaintenanceTypeToIcon = (maintenanceType: MaintenanceType) => {
  switch (maintenanceType) {
    case MaintenanceType.Deviation:
      return WarningAmberIcon
    case MaintenanceType.Service:
      return ConstructionIcon
    case MaintenanceType.Task:
      return DomainVerificationIcon
    case MaintenanceType.WorkAssignment:
      return DomainVerificationIcon
    case MaintenanceType.DailyMaintenance:
      return DomainVerificationIcon
  }
}

const MAINTENANCE_CHIP_SLUG: Partial<Record<MaintenanceType, string>> = {
  [MaintenanceType.Deviation]: 'deviation',
  [MaintenanceType.Service]: 'service',
  [MaintenanceType.Task]: 'task',
  [MaintenanceType.WorkAssignment]: 'work-assignment',
}

export const mapMaintenanceTypeToColor = (
  maintenanceType: MaintenanceType,
  colors: { error: string; primary: string },
) => {
  switch (maintenanceType) {
    case MaintenanceType.Deviation:
      return colors.error
    case MaintenanceType.Service:
    case MaintenanceType.Task:
    case MaintenanceType.WorkAssignment:
    case MaintenanceType.DailyMaintenance:
      return colors.primary
  }
}

interface IMaintenanceTypeCountBox {
  count?: number
  type: MaintenanceType
  name: string
  icon: SvgIconComponent
  color: string
  selectedMaintenanceType: MaintenanceType | undefined
  setMaintenanceType: React.Dispatch<React.SetStateAction<MaintenanceType | undefined>>
}

const MaintenanceTypeCount = ({
  count,
  type,
  name,
  icon: IconComponent,
  color,
  selectedMaintenanceType,
  setMaintenanceType,
}: IMaintenanceTypeCountBox) => {
  const theme = useTheme()
  const isSelected = type === selectedMaintenanceType
  const selectionColor = getThemeColor(
    theme,
    type === MaintenanceType.Deviation ? MUIThemeColors.errorMain : MUIThemeColors.primaryMain,
  )

  const handleClick = (type: MaintenanceType) => {
    if (type === selectedMaintenanceType) {
      setMaintenanceType(undefined)
      return
    }
    setMaintenanceType(type)
  }

  return (
    <CountCard
      data-test-id={`maintenance-chip-${MAINTENANCE_CHIP_SLUG[type] ?? type}`}
      onClick={() => handleClick(type)}
      sx={{
        borderColor: isSelected ? selectionColor : theme.palette.divider,
        backgroundColor: theme.palette.background.paper,
        borderWidth: isSelected ? 2 : 1,
        '&:hover': {
          borderColor: selectionColor,
        },
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '4px',
          backgroundColor: GetRGBA(
            mapMaintenanceTypeToColor(type, {
              error: theme.palette.error.main,
              primary: theme.palette.primaryMain[theme.palette.mode],
            }),
            0.8,
          ),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <IconComponent sx={{ fontSize: 13, color: 'common.white' }} />
      </Box>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{
          color: isSelected ? selectionColor : getThemeColor(theme, MUIThemeColors.black),
          lineHeight: 1,
        }}
      >
        {count ?? 0}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={isSelected ? 600 : 400}
        sx={{
          color: isSelected ? selectionColor : getThemeColor(theme, MUIThemeColors.secondaryMain),
          lineHeight: 1,
          fontSize: '0.75rem',
        }}
        noWrap
      >
        {name}
      </Typography>
    </CountCard>
  )
}

const calculateAllMaintenanceCount = (maintenanceCount: IMaintenanceTypeCount) => {
  const total =
    (maintenanceCount?.taskCount ?? 0) +
    (maintenanceCount?.deviationsCount ?? 0) +
    (maintenanceCount?.servicesCount ?? 0) +
    (maintenanceCount?.workOrdersCount ?? 0)
  return total
}

interface LocalityMaintenanceCardProps {
  fullWidth?: boolean
  maxHeight?: number
}

export const LocalityMaintenanceCard = ({ fullWidth, maxHeight }: LocalityMaintenanceCardProps) => {
  const theme = useTheme()
  const { t } = useTranslation(['common', 'maintenance'])
  const [maintenanceForWeek, setMaintenanceForWeek] = useState(true)
  const [maintenanceType, setMaintenanceType] = useState<MaintenanceType | undefined>()
  const { customerId, localityId } = useParams()

  const { data: maintenanceWithCounts, isLoading: maintenanceCountLoading } = useGetMaintenancesWithCounts(
    customerId as EntityId,
    localityId as EntityId,
    0,
    undefined,
    { maintenanceType: maintenanceType },
  )
  const maintenanceTypesCount = maintenanceWithCounts?.counts

  const CardComponent = fullWidth ? FullWidthStyledCard : StyledCard

  return (
    <CardComponent
      sx={maxHeight ? { height: maxHeight, maxHeight } : undefined}
      actions={
        <Box sx={{ width: '100%', pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                bgcolor: theme.palette.action.hover,
                width: 28,
                height: 28,
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img src={MaintenanceIcon} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              {t('maintenance', { ns: 'maintenance' })}
            </Typography>
          </Box>
          <CountCardContainer data-test-id="maintenance-filter-chips-container">
            <MaintenanceTypeCount
              type={MaintenanceType.Service}
              name={t('services', { ns: 'common' })}
              count={maintenanceTypesCount?.servicesCount}
              icon={ConstructionIcon}
              color={theme.palette.cerulean.main}
              setMaintenanceType={setMaintenanceType}
              selectedMaintenanceType={maintenanceType}
            />
            <MaintenanceTypeCount
              type={MaintenanceType.Deviation}
              count={maintenanceTypesCount?.deviationsCount}
              name={t('deviations', { ns: 'common' })}
              icon={WarningAmberIcon}
              color={theme.palette.error.main}
              setMaintenanceType={setMaintenanceType}
              selectedMaintenanceType={maintenanceType}
            />
            <MaintenanceTypeCount
              type={MaintenanceType.Task}
              count={maintenanceTypesCount?.taskCount}
              name={t('tasks', { ns: 'common' })}
              icon={DomainVerificationIcon}
              color={theme.palette.cerulean.main}
              setMaintenanceType={setMaintenanceType}
              selectedMaintenanceType={maintenanceType}
            />
            <MaintenanceTypeCount
              type={MaintenanceType.WorkAssignment}
              count={maintenanceTypesCount?.workOrdersCount}
              name={t('assignment', { ns: 'common' })}
              icon={AssessmentIcon}
              color={theme.palette.cerulean.main}
              setMaintenanceType={setMaintenanceType}
              selectedMaintenanceType={maintenanceType}
            />
          </CountCardContainer>
        </Box>
      }
      isLoading={maintenanceCountLoading}
    >
      <Box>
        <LocalityMaintenanceGrid
          customerId={customerId}
          localityId={localityId}
          maintenanceType={maintenanceType}
          maintenanceForWeek={maintenanceForWeek}
          totalCount={calculateAllMaintenanceCount(maintenanceTypesCount as IMaintenanceTypeCount)}
          dailyMaintenanceCount={maintenanceTypesCount?.scheduledMaintenanceDailyCount}
          maintenancesData={maintenanceWithCounts?.maintenances}
          maintenancesLoading={maintenanceCountLoading}
        />
      </Box>
    </CardComponent>
  )
}

const CountCard = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  padding: '6px 8px',
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  [theme.breakpoints.down('sm')]: {
    padding: '5px 6px',
    gap: 4,
  },
}))

const CountCardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 6,
  width: '100%',
  alignItems: 'stretch',
  [theme.breakpoints.down('sm')]: {
    gap: 4,
  },
}))

const cardBaseStyles = {
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column' as const,
  '& .MuiCardHeader-action': {
    flex: 1,
  },
  '& .MuiCardHeader-content': {
    flex: 'none',
  },
  '& .generic-card-actions': {
    paddingLeft: '0px',
  },
  '& .MuiCardContent-root': {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
}

const StyledCard = styled(GenericCard)({
  ...cardBaseStyles,
  height: 490,
})

const FullWidthStyledCard = styled(GenericCard)({
  ...cardBaseStyles,
  minHeight: 500,
  maxHeight: 'calc(100vh - 280px)',
})
